import { useState } from "react";

import { Bot, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { useAIModels } from "../api";

/** The last model the user picked, remembered across sessions. Empty = default. */
const MODEL_STORAGE_KEY = "parent_companies.ai_model";

export function storedModel(): string {
  try {
    return localStorage.getItem(MODEL_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function shortName(model: string): string {
  return model.replace(/:latest$/, "");
}

/**
 * The review-model chip + picker dialog, shared by the per-parent AI card and
 * the AI Review tab's batch runner. The pick persists in localStorage (this is
 * a per-device preference, not data), and both call sites stay in sync by
 * lifting `model` state up.
 */
export function ModelPickerButton({
  model,
  onModelChange,
  disabled = false,
}: {
  /** "" = feature default; otherwise an Ollama model name. */
  model: string;
  onModelChange: (value: string) => void;
  disabled?: boolean;
}) {
  const models = useAIModels();
  const [open, setOpen] = useState(false);

  const effective = model || models.data?.default_model || null;

  const pick = (value: string) => {
    onModelChange(value);
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, value);
    } catch {
      /* private mode — the pick just won't persist */
    }
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={model ? `Pinned to ${model}` : `Default${effective ? `: ${effective}` : ""}`}
      >
        <Bot className="h-3.5 w-3.5 text-brand" />
        {effective ? shortName(effective) : "AI"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Review model</DialogTitle>
            <DialogDescription>
              The local model that audits parent/child links. Your pick is
              remembered on this device.
            </DialogDescription>
          </DialogHeader>
          {!models.data?.reachable ? (
            <p className="text-sm text-muted-foreground">
              The local model host isn't reachable right now — AI review is
              paused, but nothing else is affected.
            </p>
          ) : (
            <div className="space-y-1.5">
              <ModelRow
                name={`Default (${models.data.default_model ? shortName(models.data.default_model) : "auto"})`}
                sizeGb={null}
                active={model === ""}
                onPick={() => pick("")}
              />
              {models.data.models.map((m) => (
                <ModelRow
                  key={m.name}
                  name={shortName(m.name)}
                  sizeGb={m.size_gb}
                  active={model === m.name}
                  recommended={m.is_default}
                  onPick={() => pick(m.name)}
                />
              ))}
              <p className="pt-1 text-xs text-muted-foreground">
                Heads-up: the default is picked for speed — bigger models can
                take minutes per review.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ModelRow({
  name,
  sizeGb,
  active,
  recommended = false,
  onPick,
}: {
  name: string;
  sizeGb: number | null;
  active: boolean;
  recommended?: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
        active ? "border-brand bg-brand/5" : "border-border hover:border-brand/50",
      )}
    >
      <span className="min-w-0 flex-1 truncate font-medium">
        {name}
        {recommended ? (
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-brand">
            default
          </span>
        ) : null}
      </span>
      {sizeGb !== null ? (
        <span className="shrink-0 text-xs text-muted-foreground">{sizeGb} GB</span>
      ) : null}
      {active ? <Check className="h-4 w-4 shrink-0 text-brand" /> : null}
    </button>
  );
}
